/**
 * PageDetailLayout - Reusable layout template for all detail/view pages.
 * Provides consistent structure: breadcrumbs, back button, header with chips/actions, tabs.
 * Used by ~10 detail pages across the application.
 */

import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Alert,
  Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface StatusConfig {
  label: string;
  color: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary' | 'secondary';
}

interface ChipConfig {
  label: string;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  variant?: 'filled' | 'outlined';
}

interface ActionConfig {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit';
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
}

interface TabConfig {
  label: string;
  value: string;
  content: ReactNode;
  disabled?: boolean;
}

interface PageDetailLayoutProps {
  /** Page title (entity name) */
  title: string;
  /** Subtitle or description */
  subtitle?: string;
  /** Breadcrumb navigation */
  breadcrumbs?: BreadcrumbItem[];
  /** Back navigation config */
  backTo?: { label: string; href: string };
  /** Status indicator */
  status?: StatusConfig;
  /** Metadata chips */
  chips?: ChipConfig[];
  /** Action buttons */
  actions?: ActionConfig[];
  /** Tabbed content */
  tabs?: TabConfig[];
  /** Default tab value */
  defaultTab?: string;
  /** Content (used when tabs are not provided) */
  children?: ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string;
  /** Error dismiss handler */
  onErrorDismiss?: () => void;
  /** Icon for the entity */
  icon?: ReactNode;
  /** Extra header content (rendered below title) */
  headerExtra?: ReactNode;
}

/**
 * Reusable detail page layout with modern glassmorphism design.
 */
export const PageDetailLayout = ({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  status,
  chips,
  actions,
  tabs,
  defaultTab,
  children,
  loading = false,
  error,
  onErrorDismiss,
  icon,
  headerExtra,
}: PageDetailLayoutProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab || (tabs?.[0]?.value ?? ''));

  // Detail pages commonly render `tabs` conditionally (e.g. only after data
  // loads), so on first mount `tabs` may be empty and `activeTab` gets stuck
  // at "". Re-sync once tabs become available, or whenever the current tab
  // is no longer in the list.
  useEffect(() => {
    if (!tabs || tabs.length === 0) return;
    const isValid = tabs.some((t) => t.value === activeTab);
    if (!isValid) {
      const fallback =
        (defaultTab && tabs.find((t) => t.value === defaultTab)?.value) ||
        tabs[0].value;
      setActiveTab(fallback);
    }
  }, [tabs, defaultTab, activeTab]);

  const handleTabChange = (_: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, animation: 'fade-in 300ms ease-out' }}>
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, bgcolor: 'var(--glass-bg-subtle)' }} />
        <Box
          sx={{
            p: 5,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            background: 'var(--glass-bg-subtle)',
            mb: 4,
          }}
        >
          <Skeleton variant="text" width={300} height={32} sx={{ mb: 2, bgcolor: 'var(--glass-bg-subtle)' }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 999, bgcolor: 'var(--glass-bg-subtle)' }} />
            <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 999, bgcolor: 'var(--glass-bg-subtle)' }} />
          </Box>
        </Box>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2, borderRadius: 'var(--mui-shape-customBorderRadius-sm)', bgcolor: 'var(--glass-bg-subtle)' }} />
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 'var(--mui-shape-customBorderRadius-lg)', bgcolor: 'var(--glass-bg-subtle)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 6, animation: 'fade-in 300ms ease-out' }}>
      {/* Back Button + Breadcrumbs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        {backTo && (
          <IconButton
            onClick={() => navigate(backTo.href)}
            size="small"
            sx={{
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              if (isLast || !crumb.href) {
                return (
                  <Typography key={crumb.label} variant="body2" color="text.primary" fontWeight={500}>
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Link
                  key={crumb.label}
                  component="button"
                  variant="body2"
                  onClick={() => crumb.href && navigate(crumb.href)}
                  sx={{
                    color: 'var(--mui-palette-text-secondary)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { color: 'var(--mui-palette-primary-main)' },
                  }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={onErrorDismiss} sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Header Card */}
      <Box
        sx={{
          p: 5,
          mb: 4,
          borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
          border: '1px solid var(--glass-border)',
          backgroundImage: 'var(--gradient-surface)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flex: 1 }}>
          {icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 'var(--mui-shape-customBorderRadius-md)',
                background: 'var(--gradient-primary-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {subtitle}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {status && (
                <Chip
                  label={status.label}
                  color={status.color}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    ...(status.color === 'success' && {
                      boxShadow: 'var(--glow-success)',
                    }),
                  }}
                />
              )}
              {chips?.map((chip, i) => (
                <Chip
                  key={i}
                  label={chip.label}
                  color={chip.color || 'default'}
                  variant={chip.variant || 'outlined'}
                  size="small"
                />
              ))}
            </Box>
            {headerExtra}
          </Box>
        </Box>
        {actions && actions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            {actions.map((action, i) => {
              if (action.variant === 'contained' || action.variant === 'outlined') {
                return (
                  <Button
                    key={i}
                    variant={action.variant}
                    color={action.color || 'primary'}
                    startIcon={action.icon}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    size="small"
                  >
                    {action.label}
                  </Button>
                );
              }
              return (
                <Tooltip key={i} title={action.label}>
                  <IconButton
                    onClick={action.onClick}
                    disabled={action.disabled}
                    color={action.color || 'default'}
                    size="small"
                    sx={{
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                    }}
                  >
                    {action.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Tabs or Content */}
      {tabs && tabs.length > 0 ? (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: 4,
              minHeight: 40,
              borderBottom: '1px solid var(--glass-border)',
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                transition: 'color 200ms ease',
                '&.Mui-selected': {
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                background: 'var(--gradient-primary)',
                height: 2,
                borderRadius: 1,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} disabled={tab.disabled} />
            ))}
          </Tabs>
          {tabs.map((tab) => (
            <Box
              key={tab.value}
              role="tabpanel"
              hidden={activeTab !== tab.value}
              sx={{ animation: activeTab === tab.value ? 'fade-in 200ms ease-out' : 'none' }}
            >
              {activeTab === tab.value && tab.content}
            </Box>
          ))}
        </>
      ) : (
        children
      )}
    </Box>
  );
};

export default PageDetailLayout;
